import type { AxiosInstance, AxiosError } from 'axios';
import type { ServiceResult } from '../types/common/ServiceResult';
import type { ServiceResultT } from '../types/common/ServiceResultT';
import type { ContentReportListItemDto } from '../types/moderation/ContentReportListItemDto';
import type { BannedUserListItemDto } from '../types/moderation/BannedUserListItemDto';
import type { AdminCreateBanRequest } from '../types/moderation/AdminCreateBanRequest';
import type { ModerationUserSearchItemDto } from '../types/moderation/ModerationUserSearchItemDto';
import { apiClient } from '../client/client';

/**
 * Admin-only moderation APIs (`/api/admin/moderation/...`).
 */
export class AdminModerationService {
  private static readonly BASE = '/api/admin/moderation';

  constructor(private readonly client: AxiosInstance = apiClient) {}

  async listReports(): Promise<ServiceResultT<ContentReportListItemDto[] | null>> {
    try {
      const { data } = await this.client.get<ServiceResultT<ContentReportListItemDto[] | null>>(
        `${AdminModerationService.BASE}/reports`
      );
      return data;
    } catch (e) {
      return this.mapError<ServiceResultT<ContentReportListItemDto[] | null>>(e);
    }
  }

  async listBans(): Promise<ServiceResultT<BannedUserListItemDto[] | null>> {
    try {
      const { data } = await this.client.get<ServiceResultT<BannedUserListItemDto[] | null>>(
        `${AdminModerationService.BASE}/bans`
      );
      return data;
    } catch (e) {
      return this.mapError<ServiceResultT<BannedUserListItemDto[] | null>>(e);
    }
  }

  async createBan(payload: AdminCreateBanRequest): Promise<ServiceResultT<string | null>> {
    try {
      const { data } = await this.client.post<ServiceResultT<string | null>>(
        `${AdminModerationService.BASE}/bans`,
        payload
      );
      return data;
    } catch (e) {
      return this.mapError<ServiceResultT<string | null>>(e);
    }
  }

  async revokeBan(banId: string): Promise<ServiceResult> {
    try {
      const { data } = await this.client.post<ServiceResult>(
        `${AdminModerationService.BASE}/bans/${banId}/revoke`
      );
      return data;
    } catch (e) {
      return this.mapError<ServiceResult>(e);
    }
  }

  /** Deferred typeahead: server requires min 2 characters. */
  async searchUsers(q: string): Promise<ServiceResultT<ModerationUserSearchItemDto[] | null>> {
    try {
      const { data } = await this.client.get<ServiceResultT<ModerationUserSearchItemDto[] | null>>(
        `${AdminModerationService.BASE}/users/search`,
        { params: { q } }
      );
      return data;
    } catch (e) {
      return this.mapError<ServiceResultT<ModerationUserSearchItemDto[] | null>>(e);
    }
  }

  private mapError<T extends ServiceResult | ServiceResultT<unknown>>(e: unknown): T {
    const err = e as AxiosError<T>;
    const body = err.response?.data;
    const status = err.response?.status ?? 0;
    if (body && typeof body === 'object' && 'isSuccess' in body) {
      return {
        ...body,
        statusCode: (body as { statusCode?: number }).statusCode ?? status,
      } as T;
    }
    return {
      isSuccess: false,
      statusCode: status,
      message: err.message ?? 'İstek başarısız.',
    } as T;
  }
}

export const adminModerationService = new AdminModerationService();
